import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'

/** Matches `yyyy-MM-dd` from the URL search param. */
const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * Serializes the calendar day the user chose in the date picker.
 * The picker supplies browser-local midnight for that cell — use local Y/M/D, not `formatInTimeZone`,
 * or the stored day shifts when the display timezone override differs from the system zone.
 */
export function formatDatePickerSelectionToParam(selection: Date): string {
    const year = selection.getFullYear()
    const month = selection.getMonth() + 1
    const day = selection.getDate()

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Resolves a search-param `date` to a single calendar day `yyyy-MM-dd` for use with `timezone`.
 * Strings pass through; missing/invalid uses "today" in `timezone`; a `Date` uses the picker's local calendar day.
 */
export function resolveCalendarDayStringInTimezone(dateInput: string | Date | undefined, timezone: string): string {
    if (typeof dateInput === 'string' && CALENDAR_DAY_PATTERN.test(dateInput)) {
        return dateInput
    }

    if (dateInput instanceof Date && !Number.isNaN(dateInput.getTime())) {
        return formatDatePickerSelectionToParam(dateInput)
    }

    return formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd')
}

/**
 * Interprets a calendar day (from URL or defaults) as midnight in `timezone`,
 * returning the corresponding UTC `Date`.
 */
export function parseCalendarDayToUtcInstant(dateInput: string | Date | undefined, timezone: string): Date {
    const dayString = resolveCalendarDayStringInTimezone(dateInput, timezone)

    return fromZonedTime(`${dayString}T00:00:00`, timezone)
}

/**
 * Interprets a calendar day (from URL or defaults) as midnight in the *browser's local* timezone.
 * Use this when feeding a date to timezone-unaware UIs (e.g. dayzed/SingleDatepicker) that call
 * `getDate()` / `setHours()` internally, so the correct calendar cell is highlighted regardless
 * of how far the app timezone diverges from the system timezone.
 */
export function parseCalendarDayToLocalMidnight(dateInput: string | Date | undefined, timezone: string): Date {
    const dayString = resolveCalendarDayStringInTimezone(dateInput, timezone)
    const [year, month, day] = dayString.split('-').map(Number)

    return new Date(year, month - 1, day)
}
